import { RegisterClass } from "core/Misc/typeStore";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

/**
 * Operations supported by the Math block
 */
export enum RewardVectorBinaryBlockOperations {
    /** Dot */
    Dot,
    /** Distance */
    Distance,
}

function OperationString(operation : RewardVectorBinaryBlockOperations) : string {
    if( typeof operation === 'number' )
        return RewardVectorBinaryBlockOperations[operation];
    return operation;
}

function StringOperation(op : string) : number {
    return (RewardVectorBinaryBlockOperations as any)[op];
}

/**
 * Block used to apply math functions
 */
export class RewardVectorBinaryBlock extends RewardGraphNode {
    /**
     * Gets or sets the operation applied by the block
     */
    @editableInPropertyPage("Operation", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "Dot", value: RewardVectorBinaryBlockOperations.Dot },
            { label: "Distance", value: RewardVectorBinaryBlockOperations.Distance },
        ],
    })
    public operation = RewardVectorBinaryBlockOperations.Dot;

    /**
     * Create a new MathBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerInput("left", RewardGraphConnectionPointTypes.Vector3);
        this.registerInput("right", RewardGraphConnectionPointTypes.Vector3);

        this.registerOutput("output", RewardGraphConnectionPointTypes.Float);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardVectorBinaryBlock";
    }

    /**
     * Gets the left input component
     */
    public get left(): RewardGraphConnectionPoint {
        return this._inputs[0];
    }

    /**
     * Gets the right input component
     */
    public get right(): RewardGraphConnectionPoint {
        return this._inputs[1];
    }

    /**
     * Gets the reward output component
     */
    public get output(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        let func: (state: RewardGraphBuildState) => any;
        const left = this.left;
        const right = this.right;

        if (!left.isConnected || !right.isConnected) {
            this.output._storedFunction = null;
            this.output._storedValue = null;
            return;
        }

        switch (this.operation) {
            case RewardVectorBinaryBlockOperations.Dot: {
                    func = (state) => {
                        const leftVal = left.getConnectedValue(state);
                        if( typeof leftVal.dot === 'function' )
                            return leftVal.dot(right.getConnectedValue(state));
                        return 0;
                    };
                break;
            }
            case RewardVectorBinaryBlockOperations.Distance: {
                    func = (state) => {
                        const leftVal = left.getConnectedValue(state);
                        if( typeof leftVal.subtract === 'function' )
                            return leftVal.subtract(state.adapt(right, left.type)).length();
                        return 0;
                    };
                break;
            }
        }

        this.output._storedFunction = (state) => {
            return func(state);
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.operation = MELS.RewardVectorBinaryBlock.${RewardVectorBinaryBlockOperations[this.operation]};\n`;
        return codeString;
    }

    /**
     * Serializes this block in a JSON representation
     * @returns the serialized block object
     */
    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.operation = OperationString(this.operation);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.operation = StringOperation(serializationObject.operation);
    }
}

RegisterClass("MELS.RewardVectorBinaryBlock", RewardVectorBinaryBlock);
